import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { rectSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import cx from 'classnames'
import isEqual from 'lodash/isEqual'

import CheckboxComponent from 'common/components/checkbox'
import IconTitleGroup from 'common/components/iconTitleGroup'
import InfoAction from 'common/components/InfoAction'
import { Topic } from 'common/components/topics/interfaces'
import { useDeepCompare } from 'common/hooks/custom'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'

import AddTopicButton from './components/addTopicButton'
import DroppableContainer from './components/DroppableContainer'
import NoTopicsOrNoMatch from './components/NoTopicsOrNoMatch'
import SortableTopicCard from './components/SortableTopicCard'
import ToggleButton from './components/ToggleButton'
import TopicCard from './components/TopicCard'
import TopicsError from './components/TopicsError'
import {
  allowConstantsClicked,
  loadError,
  toggleTopicIsConstant,
  updateContentId,
  updateTopics,
} from './store/topicsSlice'

export type TopicsProps = {
  topics: Topic[]
  fragmentsCount?: number
  isCollapsed?: boolean
  isDisabled?: boolean
  hasError?: boolean
  isCollapsible?: boolean
  onCollapseClick?: () => void
  onChange: (topics: Topic[]) => void
  className?: string
  contentId?: string
  hideNewTopicsAddButton?: boolean
  disableReplacerConfiguration?: boolean
}

const MAX_CONSTANTS_LENGTH = 2
const SUBJECT_LINE_LENGTH_THRESHOLD = 100

const dropAnimationConfig: DropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: {
        opacity: '0.5',
      },
    },
  }),
}

const Topics = ({
  topics: initialTopics,
  fragmentsCount = 0,
  isDisabled,
  isCollapsed,
  hasError = false,
  isCollapsible = false,
  onChange,
  onCollapseClick,
  className,
  contentId,
  hideNewTopicsAddButton,
  disableReplacerConfiguration = false,
}: TopicsProps) => {
  const dispatch = useAppDispatch()

  const [activeTopic, setActiveTopic] = useState<Topic | undefined>()
  const hasErrorLoadingTopics = useAppSelector(
    (state) => state.topics.hasErrorLoadingTopics
  )
  const isConfigurationMenuOpen = useAppSelector(
    (state) => state.topics.isConfigurationMenuOpen
  )
  const shouldAllowConstants = useAppSelector(
    (state) => state.topics.shouldAllowConstants
  )
  const contentStructure = useAppSelector(
    (state) => state.campaignStates.combinedLanguageSettings.contentStructure
  )
  const subjectLineLengthConfig = contentStructure?.find(
    (item) => item.name === 'Subject Line'
  )?.length
  const maxConstantsLength =
    subjectLineLengthConfig &&
    subjectLineLengthConfig < SUBJECT_LINE_LENGTH_THRESHOLD
      ? 1
      : MAX_CONSTANTS_LENGTH

  const currentTopics = useAppSelector((state) => state.topics.topics)
  const topics = useMemo(
    () => currentTopics.filter((topic) => !topic.isConstant),
    [currentTopics]
  )
  const constants = useMemo(
    () => currentTopics.filter((topic) => topic.isConstant),
    [currentTopics]
  )

  const isTopicsDragDisabled =
    constants.length >= maxConstantsLength ||
    !shouldAllowConstants ||
    isConfigurationMenuOpen ||
    isDisabled

  const isConstantsDragDisabled = isConfigurationMenuOpen || isDisabled

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  })

  const sensors = useSensors(pointerSensor)

  const onDragEnd = ({ collisions, over, active }: DragEndEvent) => {
    if (!over) {
      return
    }

    if (
      collisions?.find((c) => c.id === 'droppable-constants') &&
      active.data.current?.sortable.containerId === 'topics-context'
    ) {
      dispatch(
        toggleTopicIsConstant({
          topicId: active.id as string,
          isConstant: true,
        })
      )
    } else if (
      collisions?.find((c) => c.id === 'droppable-topics') &&
      active.data.current?.sortable.containerId === 'constants-context'
    ) {
      dispatch(
        toggleTopicIsConstant({
          topicId: active.id as string,
          isConstant: false,
        })
      )
    }

    setActiveTopic(undefined)
  }

  const onDragStart = ({ active }: DragStartEvent) => {
    const topic =
      topics.find((t) => t.id === active.id) ??
      constants.find((t) => t.id === active.id)

    setActiveTopic(topic)
  }

  const onDragCancel = () => {
    setActiveTopic(undefined)
  }

  const onAllowConstantsClick = (isChecked: boolean) => {
    dispatch(allowConstantsClicked(isChecked))
  }

  const hasInitialTopicsChanged = useDeepCompare(initialTopics)

  useEffect(() => {
    if (hasInitialTopicsChanged) {
      dispatch(updateTopics(initialTopics))
    } else if (!isEqual(currentTopics, initialTopics)) {
      onChange(currentTopics)
    }
  }, [
    currentTopics,
    dispatch,
    hasInitialTopicsChanged,
    initialTopics,
    onChange,
  ])

  useEffect(() => {
    if (contentId) {
      dispatch(updateContentId(contentId))
    }
  }, [contentId, dispatch])

  useEffect(() => {
    dispatch(loadError(hasError))
  }, [dispatch, hasError])

  const getState = () => {
    if (hasErrorLoadingTopics) {
      return 'error'
    } else if (topics.length > 0 || constants.length > 0) {
      return 'topics'
    } else if (
      topics.length === 0 &&
      constants.length === 0 &&
      fragmentsCount > 0
    ) {
      return 'noMatch'
    } else {
      return 'noTopics'
    }
  }

  const state = getState()

  return (
    <DndContext
      onDragStart={onDragStart}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
      sensors={sensors}
      measuring={{
        droppable: {
          strategy: MeasuringStrategy.Always,
        },
      }}
    >
      <div className={cx('relative overflow-hidden', className)}>
        {isCollapsed ? (
          <div className="flex">
            <div className="text-base font-medium mr-6">Topics</div>
            <div className="flex flex-wrap -mt-4">
              {topics.map(({ category, name }, index) => (
                <div
                  key={index}
                  className="text-base text-coolGray-500 font-medium mr-6 mt-4"
                >
                  {category}: {name}
                </div>
              ))}
            </div>
            <ToggleButton
              isCollapsed={isCollapsed}
              isCollapsible={isCollapsible}
              onCollapseClick={onCollapseClick}
              className="ml-auto"
            />
          </div>
        ) : (
          <>
            <div className="flex justify-between mb-1">
              <IconTitleGroup title="Topics" />
              {!hideNewTopicsAddButton && (
                <InfoAction
                  isDefaultColor={false}
                  size={4}
                  message="Use Jacquard’s topic search to add topics that match the content of your control variant."
                />
              )}
            </div>
            <div className="flex flex-row justify-between mb-4">
              <p className="text-base font-normal text-base-700">
                Topics will let us know about the specifics of your brief.
              </p>
              {!hideNewTopicsAddButton && (
                <AddTopicButton isDisabled={isDisabled} />
              )}
            </div>

            {
              {
                noTopics: <NoTopicsOrNoMatch />,
                noMatch: <NoTopicsOrNoMatch />,
                error: <TopicsError />,
                topics: (
                  <>
                    <SortableContext
                      key={topics.map((topic) => topic.id).join('')}
                      id="topics-context"
                      items={topics.map((t) => t.id)}
                      strategy={rectSortingStrategy}
                      disabled={isTopicsDragDisabled}
                    >
                      <DroppableContainer
                        id="droppable-topics"
                        data-testid="topics-list"
                        className="flex flex-wrap gap-4 overflow-hidden "
                      >
                        {topics.map((topic) => (
                          <SortableTopicCard
                            topic={topic}
                            key={topic.id}
                            isDisabled={isDisabled}
                            isDragDisabled={isTopicsDragDisabled}
                            disableReplacerConfiguration={
                              disableReplacerConfiguration
                            }
                            className={cx({
                              'cursor-auto': !shouldAllowConstants,
                              'cursor-pointer': shouldAllowConstants,
                            })}
                          />
                        ))}
                      </DroppableContainer>
                    </SortableContext>
                    <div className="mt-8">
                      <CheckboxComponent
                        label="Allow Focus Themes"
                        data-testid="allow-focus-themes"
                        isChecked={shouldAllowConstants}
                        onChange={onAllowConstantsClick}
                        isDisabled={isDisabled}
                        className="w-max mb-6"
                      />
                      <p
                        className={cx(
                          'text-base-700 text-base fon font-normal'
                        )}
                      >
                        Focus Themes increase messaging specificity around the
                        selected topics.
                      </p>
                    </div>
                    {shouldAllowConstants && (
                      <SortableContext
                        id="constants-context"
                        items={constants.map((t) => t.id)}
                        strategy={rectSortingStrategy}
                        disabled={isConstantsDragDisabled}
                      >
                        <DroppableContainer
                          id="droppable-constants"
                          data-testid="focused-topics-list"
                          className="flex flex-wrap gap-4 p-4 min-h-24 border border-gold-400 relative rounded-sm mt-4 items-center"
                        >
                          <div
                            className="absolute text-base-700 text-base font-medium opacity-50
                            top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                          >
                            {constants.length === 0
                              ? 'Drag your topics here'
                              : ''}
                          </div>
                          {constants.map((constant) => (
                            <SortableTopicCard
                              topic={constant}
                              key={constant.id}
                              isDisabled={isDisabled}
                              disableReplacerConfiguration={
                                disableReplacerConfiguration
                              }
                              isDragged
                            />
                          ))}
                        </DroppableContainer>
                      </SortableContext>
                    )}
                    {createPortal(
                      <DragOverlay dropAnimation={dropAnimationConfig}>
                        {activeTopic ? (
                          <TopicCard
                            style={{ boxShadow: '0 25px 30px 0 #9ca3af' }}
                            topic={activeTopic}
                            isActive
                            disableReplacerConfiguration={
                              disableReplacerConfiguration
                            }
                          />
                        ) : null}
                      </DragOverlay>,
                      document.body
                    )}
                  </>
                ),
              }[state]
            }
            <div className="w-full flex justify-end">
              <ToggleButton
                isCollapsed={isCollapsed}
                isCollapsible={isCollapsible}
                onCollapseClick={onCollapseClick}
                className="mt-4"
              />
            </div>
          </>
        )}
      </div>
    </DndContext>
  )
}

export default Topics
